import { Button, MenuItem, Select, Skeleton, Typography, styled, useTheme } from 'opensolar-ui'
import { useMemo } from 'react'
import type { ContractTemplateListItem } from '../../utils/contractTemplateUtils'
import { buildContractSelectSections } from '../../utils/entitySharingUtils'
import type { ProjectContractSelectProps } from './types'
import renderGroupedContractMenuItems from './GroupedContractMenuItems'

const Container = styled('div')(
  {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
    gap: '8px',
    '@media (max-width: 599px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  { name: 'ProjectContractSelectContainer' }
)

const ButtonContainer = styled('div')(
  {
    display: 'flex',
    flexGrow: 0,
    alignItems: 'center',
    maxWidth: '130px',
    textAlign: 'center',
    fontSize: '12px',
    '@media (max-width: 599px)': {
      width: '100%',
      maxWidth: 'none',
      paddingRight: 0,
    },
    '& button': {
      '@media (max-width: 599px)': {
        width: '100%',
      },
      fontSize: '12px',
      lineHeight: 'normal',
      padding: '7px',
    },
  },
  { name: 'ProjectContractSelectButtonContainer' }
)

const EmptyOption = styled('span')(
  {
    color: '#aaa',
    fontStyle: 'italic',
  },
  { name: 'ProjectContractSelectEmptyOption' }
)

const FullWidthSelect = styled(Select)(
  {
    width: '100%',
    minWidth: '160px',
    maxWidth: '180px',
    margin: '1rem 1.2rem 1rem 1.5rem',
    flexGrow: 1,
    '@media (max-width: 599px)': {
      width: '100%',
      margin: '1rem 0',
      maxWidth: 'none',
    },
  },
  { name: 'ProjectContractSelectFullWidthSelect' }
)

const ProjectContractSelect = ({
  translate,
  disabled,
  contractsLoading,
  contracts,
  projectContractUrl,
  onProjectContractChange,
  currentOrgId,
  currentOrgUrl,
  projectOrgId,
  isSharedProject,
  onEditContract,
  groupingContext,
}: ProjectContractSelectProps) => {
  const theme = useTheme()

  const sections = useMemo(() => buildContractSelectSections(contracts, groupingContext), [contracts, groupingContext])

  const selectedContractTemplate = useMemo(() => {
    if (!projectContractUrl) return null
    return contracts?.find((contract) => contract.url === projectContractUrl) ?? null
  }, [contracts, projectContractUrl])

  const defaultContractText = useMemo(() => {
    const defaultContract = contracts?.find((contract) => contract.is_default)
    const defaultEnd = defaultContract
      ? ` (${defaultContract.title})`
      : currentOrgId !== projectOrgId
        ? ` (${translate('Unshared Item')})`
        : ''
    return translate('Default Template') + defaultEnd
  }, [contracts, translate, currentOrgId, projectOrgId])

  const buttonLabel = useMemo(() => {
    if (isSharedProject) {
      return (selectedContractTemplate as ContractTemplateListItem & { org?: string })?.org === currentOrgUrl
        ? translate('Edit Contract Template')
        : translate('View Contract Template')
    }
    return translate('Edit Contract Template')
  }, [isSharedProject, selectedContractTemplate, currentOrgUrl, translate])

  const showContractsLoading = contractsLoading && !contracts?.length

  if (showContractsLoading) {
    return (
      <>
        <Typography variant="subtitle2" colorHex={theme.palette.neutralGrey[900]}>
          {translate('Contract Template')}
        </Typography>
        <Skeleton width="100%" height={44} />
      </>
    )
  }

  return (
    <Container>
      <FullWidthSelect
        label={translate('Contract Template')}
        variant="outlined"
        disabled={disabled}
        value={projectContractUrl || ''}
        onChange={(e) => onProjectContractChange(e.target.value as string)}
        displayEmpty
        renderValue={(value) => {
          if (value === '') {
            return <EmptyOption>{defaultContractText}</EmptyOption>
          }
          return selectedContractTemplate?.title || ''
        }}
      >
        <MenuItem value="">
          <EmptyOption>{defaultContractText}</EmptyOption>
        </MenuItem>
        {sections
          ? renderGroupedContractMenuItems({
              sections,
              getValue: (contract) => contract.url || '',
              translate,
            })
          : contracts?.map((contract) => (
              <MenuItem key={contract.id} value={contract.url}>
                {contract.title}
              </MenuItem>
            ))}
      </FullWidthSelect>
      {projectContractUrl && selectedContractTemplate && (
        <ButtonContainer>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            style={{ textTransform: 'none' }}
            onClick={() => onEditContract(selectedContractTemplate)}
          >
            <span>{buttonLabel}</span>
          </Button>
        </ButtonContainer>
      )}
    </Container>
  )
}

export default ProjectContractSelect
